import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Upload } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useFileUpload } from '../hooks/useSalesDocs';
import { deriveFileName } from '../constants';
import type { CreateSalesDocPayload, SalesDoc, UpdateSalesDocPayload } from '../types';

interface SalesDocFormModalProps {
  open: boolean;
  doc?: SalesDoc | null; // null = create
  loading?: boolean;
  onClose: () => void;
  onCreate: (payload: CreateSalesDocPayload) => void;
  onUpdate: (id: string, payload: UpdateSalesDocPayload) => void;
}

export function SalesDocFormModal({ open, doc, loading, onClose, onCreate, onUpdate }: SalesDocFormModalProps) {
  const isEdit = !!doc;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useFileUpload();

  useEffect(() => {
    if (!open) return;
    setTitle(doc?.title ?? '');
    setDescription(doc?.description ?? '');
    setUrl(doc?.url ?? '');
  }, [open, doc]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (returnedUrl) => {
        setUrl(returnedUrl);
        if (!title.trim()) setTitle(file.name);
        toast.success('File uploaded.');
      },
      onError: () => toast.error('Upload failed.'),
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (!url.trim()) {
      toast.error('Add a document URL or upload a file.');
      return;
    }
    const payload: CreateSalesDocPayload = { title: title.trim(), description: description.trim(), url: url.trim() };
    if (isEdit && doc) onUpdate(doc.id, payload);
    else onCreate(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Document' : 'New Document'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="salesdoc-form" loading={loading}>{isEdit ? 'Save Changes' : 'Add Document'}</Button>
        </>
      }
    >
      <form id="salesdoc-form" onSubmit={submit} className="space-y-4">
        <Input label="Title *" name="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title…" required />

        <div>
          <label htmlFor="doc-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea
            id="doc-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div>
          <Input
            label="Document URL *"
            name="doc-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… or upload a file"
            required
          />
          <div className="mt-2 flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Upload file
            </Button>
            {url && <span className="truncate text-body-sm text-on-surface-variant">{deriveFileName(url)}</span>}
          </div>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
        </div>
      </form>
    </Modal>
  );
}
